import React, { useState, useEffect } from 'react';
import { Users, Calendar, CheckCircle, XCircle, Building2, User } from 'lucide-react';
import { CustomerGroup } from '../../../types/customerGroup';
import { formatDistanceToNow } from 'date-fns';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import customerService from '../../../services/customerService';
import { Customer } from '../../../services/customerService';
import { LoadingSpinner } from '../../../shared/LoadingSpinner';

interface CustomerGroupDetailsDrawerProps {
  customerGroup: CustomerGroup | null;
  isOpen: boolean;
  onClose: () => void;
}

const CustomerGroupDetailsDrawer: React.FC<CustomerGroupDetailsDrawerProps> = ({
  customerGroup,
  isOpen,
  onClose,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!customerGroup?.customerId || customerGroup.customerId.length === 0) {
        setCustomers([]);
        return;
      }

      setIsLoadingCustomers(true);
      try {
        // Fetch all customers and filter by IDs
        const response = await customerService.getCustomers({
          status: 'active',
        });
        
        const allCustomers = response.data || [];
        const customerIds = customerGroup.customerId || [];
        const matchedCustomers = allCustomers.filter(c => customerIds.includes(c._id));
        setCustomers(matchedCustomers);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    if (isOpen && customerGroup) {
      fetchCustomers();
    }
  }, [isOpen, customerGroup]);

  if (!customerGroup) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const isActive = status === 'active';
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${
          isActive
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-gray-50 text-gray-600 border-gray-200'
        }`}
      >
        {isActive ? (
          <CheckCircle size={14} className="text-green-600" />
        ) : (
          <XCircle size={14} className="text-gray-500" />
        )}
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const InfoRow = ({ 
    icon: Icon, 
    label, 
    value 
  }: { 
    icon: React.ElementType; 
    label: string; 
    value: string | undefined | null; 
  }) => {
    if (!value || value === 'N/A') return null;

    return (
      <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 shadow-sm flex items-center justify-center">
            <Icon className="w-5 h-5 text-gray-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            {label}
          </div>
          <div className="text-sm font-medium text-gray-900 break-words">
            {value}
          </div>
        </div>
      </div>
    );
  };

  return (
    <RightDrawer open={isOpen} onClose={onClose} title="Customer Group Details" size="lg">
      <div className="h-full flex flex-col">
        {/* Header Section */}
        <div className="flex items-center gap-4 p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
            <Users size={32} className="text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 truncate">
              {customerGroup.customerGroupName}
            </h2>
            <div className="mt-2">
              {getStatusBadge(customerGroup.status)}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Customer Group Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <Users size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Customer Group Information</h3>
              </div>
              
              <div className="space-y-3">
                <InfoRow
                  icon={Users}
                  label="Customer Group Name"
                  value={customerGroup.customerGroupName}
                />
              </div>
            </div>

            {/* Customers */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <Building2 size={20} className="text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Customers ({customers.length})
                </h3>
              </div>
              
              {isLoadingCustomers ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : customers.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-4">
                  No customers assigned to this group
                </div>
              ) : (
                <div className="space-y-3">
                  {customers.map((customer) => (
                    <div
                      key={customer._id}
                      className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/50 transition-colors border border-gray-100 bg-white/30"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        {customer.companyName ? (
                          <>
                            <div className="font-semibold text-base text-gray-900">{customer.companyName}</div>
                            <div className="text-sm text-gray-600 mt-0.5">{customer.name}</div>
                          </>
                        ) : (
                          <div className="font-medium text-gray-900">{customer.name}</div>
                        )}
                        {customer.email && (
                          <div className="text-xs text-gray-500 mt-1">{customer.email}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* System Information */}
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={20} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">System Information</h3>
              </div>
              
              <div className="space-y-3">
                <InfoRow
                  icon={Calendar}
                  label="Created At"
                  value={customerGroup.createdAt ? formatDate(customerGroup.createdAt) : undefined}
                />
                <InfoRow
                  icon={Calendar}
                  label="Last Updated"
                  value={customerGroup.updatedAt ? formatDate(customerGroup.updatedAt) : undefined}
                />
                {customerGroup.createdAt && (
                  <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-100">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Created
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDistanceToNow(new Date(customerGroup.createdAt), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </RightDrawer>
  );
};

export default CustomerGroupDetailsDrawer;
